import { CloudRainWind, Droplet, Minus, Umbrella } from "lucide-react";
import { DateTime } from "luxon";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import "twin.macro";

import { ClimateDataGranularity, RainfallData } from "@/api/climate-data/types";
import {
  Card,
  ClimateRangeControls,
  InfoTooltip,
  SmallStatCard,
  useClimateRange,
} from "./range-selector";
import {
  RainfallDataLoader,
  useRainfallDataContext,
} from "../data-loaders/rainfall-data-loader";
import {
  ProtectionPeriod,
  ProtectionPeriodComponent,
  ProtectionPeriodList,
} from "./protection-period-list.component";
import {
  ProtectionLegendItem,
  formatChartTick,
  renderProtectionAreas,
} from "./protection-chart.component";

const COLOR_TOTAL = "#1E3A8A";
const COLOR_EFFECTIF = "#3B82F6";

type RainfallProtectionPeriod = ProtectionPeriod & {
  protectedMm: number;
};

type RainfallProtectionPeriodListPropsBase = {
  periods: RainfallProtectionPeriod[];
  granularity: "hour" | "day";
};

const RainfallProtectionPeriodList = (
  props: RainfallProtectionPeriodListPropsBase
) => {
  return (
    <ProtectionPeriodList periods={props.periods}>
      {props.periods.map((p, i) => (
        <ProtectionPeriodComponent
          key={`${p.from}-${i}`}
          from={p.from}
          to={p.to}
          isLast={i === props.periods.length - 1}
        >
          <div tw="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium shrink-0">
            <Minus tw="w-3 h-3" />
            {formatMm(p.protectedMm)} mm
          </div>
        </ProtectionPeriodComponent>
      ))}
    </ProtectionPeriodList>
  );
};

const formatMm = (value: number) =>
  value.toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const CumulEffectifCard = ({ value }: { value: number | null }) => {
  const { t } = useTranslation();
  return (
    <Card tw="flex items-center gap-3 px-4 py-3">
      <div tw="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
        <Droplet tw="w-6 h-6 text-blue-500" />
      </div>
      <div tw="flex flex-col">
        <p tw="text-xs text-gray-500 flex items-center gap-1">
          {t("meteo.climateView.stats.effectiveCumul")}
          <InfoTooltip text={t("meteo.climateView.stats.effectiveCumulDesc")} />
        </p>
        <p tw="text-3xl font-bold text-blue-500">
          {value === null ? "—" : `${formatMm(value)} mm`}
        </p>
      </div>
    </Card>
  );
};

type ChartPoint = { ts: number; total: number; effectif: number };

const buildChartData = (
  series: RainfallData["series"],
  windowFrom: string
): ChartPoint[] => {
  let total = 0;
  let effectif = 0;
  const points = series.map((point) => {
    total += point.mm;
    effectif += point.effectiveMm;
    return {
      ts: DateTime.fromISO(point.timestamp).toMillis(),
      total: Number(total.toFixed(2)),
      effectif: Number(effectif.toFixed(2)),
    };
  });

  // Ancre la courbe cumulative à 0 au début de la fenêtre. Nécessaire surtout
  // en granularité `hour`, où le `timestamp` d'un bucket pointe sur sa FIN : le
  // premier point tombe alors après `windowFrom`, et sans cette ancre la courbe
  // démarrerait au-dessus de 0. On l'ajoute uniquement si aucun point ne couvre
  // déjà le début de fenêtre (cas `day` avec bucket aligné sur `from`).
  const fromMs = DateTime.fromISO(windowFrom).toMillis();
  if (points.length === 0 || points[0].ts > fromMs) {
    points.unshift({ ts: fromMs, total: 0, effectif: 0 });
  }

  return points;
};

const RainfallChart = ({
  series,
  protectedPeriods,
  granularity,
  windowFrom,
}: {
  series: RainfallData["series"];
  protectedPeriods: RainfallData["protectedPeriods"];
  granularity: ClimateDataGranularity;
  windowFrom: string;
}) => {
  const { t } = useTranslation();
  const chartData = useMemo(
    () => buildChartData(series, windowFrom),
    [series, windowFrom]
  );

  const yMax = useMemo(() => {
    const max = chartData.reduce((m, p) => Math.max(m, p.total), 0);
    return Math.max(10, Math.ceil(max / 10) * 10);
  }, [chartData]);

  const formatTick = (ts: number) => formatChartTick(ts, granularity);

  return (
    <Card tw="p-4 flex flex-col gap-4">
      <div tw="flex items-baseline gap-2">
        <p tw="text-base font-semibold text-gray-800">
          {t("meteo.climateView.chart.title")}
        </p>
        <span tw="text-xs text-gray-400">
          {t("meteo.climateView.chart.unit")}
        </span>
      </div>

      <div tw="w-full h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 8, left: -16, bottom: 8 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E7EB"
            />
            {renderProtectionAreas(protectedPeriods)}
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={formatTick}
              tick={{ fill: "#6B7280", fontSize: 10 }}
              stroke="#9CA3AF"
              tickLine={false}
              axisLine={false}
              height={30}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fill: "#6B7280", fontSize: 10 }}
              stroke="#9CA3AF"
              tickLine={false}
              axisLine={false}
              label={{
                value: "mm",
                position: "insideStart",
                fill: "#6B7280",
                fontSize: 10,
                offset: 10,
              }}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke={COLOR_TOTAL}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="effectif"
              stroke={COLOR_EFFECTIF}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div tw="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 pt-2 border-t border-gray-100">
        <span tw="flex items-center gap-2">
          <span
            tw="inline-block w-4 h-0.5 rounded-full"
            style={{ backgroundColor: COLOR_TOTAL }}
          />
          {t("meteo.climateView.chart.legendTotal")}
        </span>
        <span tw="flex items-center gap-2">
          <span
            tw="inline-block w-4 h-0.5 rounded-full"
            style={{ backgroundColor: COLOR_EFFECTIF }}
          />
          {t("meteo.climateView.chart.legendEffective")}
        </span>
        <ProtectionLegendItem />
      </div>
    </Card>
  );
};

function RainfallContent({
  granularity,
}: {
  granularity: ClimateDataGranularity;
}) {
  const { t } = useTranslation();
  const { data, isFetching } = useRainfallDataContext();

  return (
    <Fragment>
      <CumulEffectifCard value={data.window.effectiveMm} />
      <div tw="flex gap-3">
        <SmallStatCard
          icon={<CloudRainWind tw="w-5 h-5 text-blue-500" />}
          iconBg="#DBEAFE"
          label={t("meteo.climateView.stats.totalCumul")}
          infoText={t("meteo.climateView.stats.totalCumulDesc")}
          value={`${formatMm(data.window.totalMm)} mm`}
          valueColor="#1F2937"
        />
        <SmallStatCard
          icon={<Umbrella tw="w-5 h-5 text-emerald-600" />}
          iconBg="#D1FAE5"
          label={t("meteo.climateView.stats.deflected")}
          infoText={t("meteo.climateView.stats.deflectedDesc")}
          value={`${formatMm(data.window.protectedMm)} mm`}
          valueColor="#059669"
        />
      </div>
      <RainfallChart
        series={data.series}
        protectedPeriods={data.protectedPeriods}
        granularity={granularity}
        windowFrom={data.window.from}
      />

      <RainfallProtectionPeriodList
        periods={data.protectedPeriods}
        granularity={granularity}
      />

      {isFetching && (
        <p tw="text-xs text-gray-400 text-center">
          {t("meteo.climateView.refreshing")}
        </p>
      )}
    </Fragment>
  );
}

export default function RainfallDataSheetBody({ areaId }: { areaId: string }) {
  const range = useClimateRange();
  const { appliedRange } = range;

  return (
    <div tw="flex flex-col gap-4 px-4 pb-6">
      <ClimateRangeControls range={range} />
      <RainfallDataLoader
        areaId={areaId}
        from={appliedRange.from}
        to={appliedRange.to}
        granularity={appliedRange.granularity}
      >
        <RainfallContent granularity={appliedRange.granularity} />
      </RainfallDataLoader>
    </div>
  );
}
